import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import { caseHasDiscoveryEvidence } from './PathUtils.js';

const MEASUREMENT_ID = 'G-N6TMN0F3DE';
const GOOGLE_ADS_ID = 'AW-18410784637';

/** Marketing and auth-entry routes that may load the Google Ads tag. */
const PUBLIC_EXACT_ROUTES = new Set([
    '/',
    '/about',
    '/contact',
    '/pricing',
    '/press',
    '/privacy',
    '/security',
    '/terms',
    '/register',
    '/verify-email',
    '/forgot-password',
    '/reset-password'
]);

function isEnabled() {
    return process.env.NODE_ENV === 'production'
        && process.env.REACT_APP_GA_ENABLED !== 'false';
}

function isDevelopment() {
    return process.env.NODE_ENV === 'development';
}

function logAnalytics(eventName, params = {}) {
    if (isDevelopment()) {
        console.log('[analytics]', eventName, params);
    }
}

let initialized = false;
let adsTagConfigured = false;

/**
 * Whether the Google Ads global site tag may load on this route.
 * Authenticated app routes (/mycases, /case/*, /account, etc.) are excluded.
 */
export function isPublicRoute(pathname) {
    if (PUBLIC_EXACT_ROUTES.has(pathname)) {
        return true;
    }
    return pathname.startsWith('/press/') && pathname.length > '/press/'.length;
}

/**
 * Normalize paths for GA aggregation (UUIDs and slugs → route templates).
 */
export function sanitizePath(pathname) {
    return pathname
        .replace(/\/case\/[^/]+/, '/case/:caseId')
        .replace(/\/press\/[^/]+/, '/press/:slug');
}

export function getCaseTabSlugs(caseInfo) {
    const showDiscovery = caseHasDiscoveryEvidence(caseInfo);
    const showIncidentDetails = (caseInfo?.documents
        && Object.keys(caseInfo.documents).some(
            (filename) => caseInfo.documents[filename].analysis?.incident_details?.length > 0
        ))
        || (caseInfo?.analysis?.incident_summary?.incident_date
            && !Number.isNaN(new Date(caseInfo.analysis.incident_summary.incident_date).getTime()));

    const tabs = ['documents'];
    if (showIncidentDetails) {
        tabs.push('incident_details');
    }
    tabs.push('medical_chronology', 'medical_expenses', 'timeline');
    if (showDiscovery) {
        tabs.push('discovery');
    }
    tabs.push('drafting');
    return tabs;
}

function configureAdsTag() {
    if (!isEnabled() || adsTagConfigured) {
        return;
    }
    ReactGA.gtag('config', GOOGLE_ADS_ID);
    adsTagConfigured = true;
    logAnalytics('ads_tag_configured', { ads_id: GOOGLE_ADS_ID });
}

/** Load AW tag when the user is on a public route (for ad attribution/remarketing). */
export function syncAdsTagForRoute(pathname) {
    if (isPublicRoute(pathname)) {
        configureAdsTag();
    }
}

export function initAnalytics() {
    if (!isEnabled() || initialized) {
        return;
    }
    ReactGA.initialize(MEASUREMENT_ID);
    initialized = true;
    syncAdsTagForRoute(window.location.pathname);
}

/**
 * Parse GA4 client_id from the _ga cookie (GA1.1.{part1}.{part2} → "{part1}.{part2}").
 * @returns {string|null}
 */
export function getGaClientIdFromCookie() {
    if (typeof document === 'undefined') {
        return null;
    }
    const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/);
    return match ? match[1] : null;
}

/**
 * Align browser GA4 identity with server-stored ids after authentication.
 * @param {{ gaClientId?: string|null, gaUserId?: string|null }} ids
 */
export function syncAnalyticsIdentity({ gaClientId, gaUserId }) {
    const fields = {};
    if (gaClientId) {
        fields.clientId = gaClientId;
    }
    if (gaUserId) {
        fields.userId = gaUserId;
    }
    if (Object.keys(fields).length === 0) {
        return;
    }
    logAnalytics('sync_identity', fields);
    if (!isEnabled()) {
        return;
    }
    ReactGA.set(fields);
}

function gaClientIdRequestBody() {
    const gaClientId = getGaClientIdFromCookie();
    return gaClientId ? { gaClientId } : {};
}

export { gaClientIdRequestBody };

export function trackPageView(pathname) {
    const page = sanitizePath(pathname);
    const publicRoute = isPublicRoute(pathname);
    logAnalytics('pageview', { page, title: page, public_route: publicRoute });
    if (!isEnabled()) {
        return;
    }
    syncAdsTagForRoute(pathname);
    ReactGA.gtag('event', 'page_view', {
        page_path: page,
        page_title: page,
        send_to: publicRoute ? [MEASUREMENT_ID, GOOGLE_ADS_ID] : MEASUREMENT_ID,
    });
}

export function trackEvent(eventName, params = {}) {
    logAnalytics(eventName, params);
    if (!isEnabled()) {
        return;
    }
    ReactGA.event(eventName, {
        ...params,
        send_to: MEASUREMENT_ID,
    });
}

export function trackLogin(method) {
    trackEvent('login', { method });
}

/** Client intent: user starts Stripe Checkout (trial or paid). */
export function trackBeginCheckout({ planId, hasTrial }) {
    trackEvent('begin_checkout', {
        plan_id: planId,
        has_trial: Boolean(hasTrial),
    });
}

/** User selects a plan on the public pricing page. */
export function trackPricingPlanClick({ planId, destination }) {
    trackEvent('pricing_plan_click', {
        plan_id: planId,
        destination,
    });
}

export function trackCreateCase() {
    trackEvent('create_case');
}

export function trackUploadCaseDocuments(fileCount) {
    trackEvent('upload_case_documents', {
        file_count: fileCount,
    });
}

export function trackViewTab(tabName) {
    trackEvent('view_tab', { tab_name: tabName });
}

export function trackCopyContent(contentType) {
    trackEvent('copy_content', { content_type: contentType });
}

export function trackDocumentChoicesPanelClick(action) {
    trackEvent('document_choices_panel_click', { action });
}

export function trackDocumentToggle({ category, included }) {
    trackEvent('document_toggle_click', {
        category,
        included,
    });
}

/**
 * Final path segment from a JSONPath textPath (e.g. "...patient_name" → "patient_name").
 */
export function getFieldNameFromTextPath(textPath) {
    if (!textPath || typeof textPath !== 'string') {
        return 'unknown';
    }

    const dotSegment = textPath.match(/\.([a-zA-Z_][a-zA-Z0-9_]*)$/);
    if (dotSegment) {
        return dotSegment[1];
    }

    const quotedKey = textPath.match(/\['([^']+)'\]$|\["([^"]+)"\]$/);
    if (quotedKey) {
        return quotedKey[1] ?? quotedKey[2];
    }

    const indexSegment = textPath.match(/\[(\d+)\]$/);
    if (indexSegment) {
        return indexSegment[1];
    }

    return 'unknown';
}

export function trackEditField(fieldName) {
    trackEvent('edit_field', { field_name: fieldName });
}

export function trackWebVital({ name, delta, id, value }) {
    trackEvent('web_vitals', {
        metric_name: name,
        metric_id: id,
        metric_value: Math.round(name === 'CLS' ? value * 1000 : value),
        metric_delta: Math.round(name === 'CLS' ? delta * 1000 : delta),
    });
}

/**
 * Sends a sanitized page_view on each SPA route change.
 */
export function AnalyticsTracker() {
    const { pathname } = useLocation();

    useEffect(() => {
        trackPageView(pathname);
    }, [pathname]);

    return null;
}
